// feature/smart-refresh — listen for the global "eds:refresh" CustomEvent dispatched
// by TopBar when new notifications arrive, and re-fetch view data.
//
// `relevantTypes` is an array of notification.type strings the view cares
// about. If any of them appears in event.detail.types, the callback fires.
// Pass an empty array (or null) to refresh on every event.
//
// Defers the refresh while the user is typing in an input/textarea so we
// don't wipe their in-progress text. The hook stashes the pending event and
// fires it once focus moves away.
import { useEffect, useRef } from "react";

export function useRefreshOnNotification(relevantTypes, onRefresh) {
  const pendingRef = useRef(false);

  useEffect(() => {
    function shouldFire(types) {
      if (!relevantTypes || relevantTypes.length === 0) return true;
      if (!Array.isArray(types) || !types.length) return false;
      return types.some(t => relevantTypes.includes(t));
    }
    function isUserTyping() {
      const el = document.activeElement;
      if (!el) return false;
      const tag = (el.tagName || "").toLowerCase();
      return tag === "input" || tag === "textarea" || el.isContentEditable;
    }
    function onEvt(e) {
      if (!shouldFire(e?.detail?.types)) return;
      if (isUserTyping()) {
        pendingRef.current = true;
        return;
      }
      onRefresh();
    }
    function onFocusOut() {
      if (pendingRef.current) {
        pendingRef.current = false;
        onRefresh();
      }
    }
    window.addEventListener("eds:refresh", onEvt);
    window.addEventListener("focusout", onFocusOut);
    return () => {
      window.removeEventListener("eds:refresh", onEvt);
      window.removeEventListener("focusout", onFocusOut);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(relevantTypes), onRefresh]);
}
